/**
 * FOOTHILLS-SOUTH — roster wave entries. GENERATED BY ASSEMBLY, WRITTEN BY REVIEW.
 *
 * Geography (streets, landmarks, coordinates, adjacency) ported from the source
 * roster; airspace derived by the calibrated classifier in
 * scripts/derive-airspace.mjs; every descriptive sentence written fresh from
 * aerial-media truth and fact-checked against its generation brief. See
 * docs/geo/VISION-v3.md for the fact contract these entries were written under.
 *
 * 6 entries. Edit by hand freely — assembly never overwrites a
 * file that has drifted from its fragments without --force.
 */
import type { Location } from "../types";

export const ROSTER_FOOTHILLS_SOUTH: Location[] = [
// ────────────────────────────────────────────────────────────── ALDERSYDE ──
  {
    slug: "aldersyde",
    name: "Aldersyde",
    region: "foothills-south",
    city: "Foothills County",
    province: "Alberta",
    coordinates: { lat: 50.6942, lng: -113.9236 },
    tier: 4,
    driveMinutesFromCalgary: 40,
    airspace: {
      classification: "Class G uncontrolled, local aerodrome nearby",
      nearestAerodrome: "Okotoks Air Park",
      distanceToAerodromeKm: 4,
      authorizationRequired: false,
      authorizationNote:
        "In uncontrolled Class G airspace about 4 km from Okotoks Air Park, Aldersyde lies outside the charted Calgary-area control zones, and no authorisation is normally required. Okotoks Air Park runs local circuit traffic nearby, so flights are planned with its procedures in mind. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "From the air, Aldersyde is a handful of streets — Niblock St, Elliott St — set against Highway 2A, with 418 Ave E and 426 Ave E striking out from it in the straight lines of the county survey.",
      consequence:
        "A hamlet this small gives a ground camera almost nothing to work with: the frame shows a house and a road, and none of the direct highway access that makes an acreage or yard site here worth what it is.",
      resolution:
        "A boundary-line orbit planned to hold the property, its approach road and Highway 2A in one frame puts that access on the record, delivered in the standard turnaround.",
    },
    landmarks: [
      "Highway 2A",
      "Niblock St",
      "Elliott St",
      "Aldersyde Hall",
      "Sheep River",
    ],
    terrain:
      "Level crop and ranch land on the county survey grid, with the Sheep River's shallow, treed valley crossing north of the hamlet and the foothills a distant line west.",
    windNote:
      "Chinook belt, and open. West wind crosses the flat ground here with little in its way; the Sheep River's shallow valley offers a brief break, not shelter.",
    seasonalWindow:
      "Long, unobstructed light at both ends of the day on this open ground. The surrounding quarters carry their strongest colour in the weeks around harvest, and fresh snow flattens them to white within a day.",
    bylawNote:
      "Foothills County's park and public-land rules govern launch and landing on municipal ground; takeoff from private property with the owner's consent is the working default.",
    subjectMix: ["acreage-estate", "agricultural", "municipal-infrastructure"],
    mobilisationNote:
      "About 40 minutes south of the Calgary base. Comfortably a same-day booking, and Okotoks or High River can share the same run.",
    faqs: [
      {
        question: "Does traffic at Okotoks Air Park affect drone flights in Aldersyde?",
        answer:
          "Aldersyde sits in uncontrolled Class G airspace, outside the charted Calgary-area control zones, so no airspace authorisation is normally required. Okotoks Air Park is about 4 km away, though, so flights here are planned with the aerodrome's circuit procedures in mind, and the specific site and altitude are confirmed against NAV Drone before every flight.",
      },
      {
        question: "Can one aerial frame show both an Aldersyde property and its highway access?",
        answer:
          "Yes — that is usually the point of flying here. An orbit planned off the property's boundary can hold the yard site, its approach road and Highway 2A in a single readable frame, so a buyer sees at a glance how directly the property connects to the corridor.",
      },
    ],
    primaryKeywords: [
      "drone photography Aldersyde",
      "aerial photography Aldersyde",
      "Aldersyde drone services",
    ],
    nearestLocations: ["okotoks", "high-river", "cayley", "de-winton"],
  },

  // ───────────────────────────────────────────────────────────────── CAYLEY ──
  {
    slug: "cayley",
    name: "Cayley",
    region: "foothills-south",
    city: "Foothills County",
    province: "Alberta",
    coordinates: { lat: 50.4614, lng: -113.9231 },
    tier: 4,
    driveMinutesFromCalgary: 60,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "High River aerodrome",
      distanceToAerodromeKm: 10,
      authorizationRequired: false,
      authorizationNote:
        "In uncontrolled Class G airspace about 10 km from High River aerodrome, Cayley lies outside the charted Calgary-area control zones, and no authorisation is normally required. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "From above, Cayley's biggest built footprints are not its houses — Cayley Hall and the Stampede Grounds Cayley outsize everything else, and the hamlet's few short blocks sit off Cayley Access Rd in a tight square with open quarters on every side.",
      consequence:
        "That abrupt edge means a listing photographed from the street borrows no streetscape at all — the frame cannot say whether the property sits in a community or alone on the prairie.",
      resolution:
        "A rising reveal from lot height to above the rooflines shows both truths at once — the hamlet's compact grid and the working land around it — delivered in the standard turnaround.",
    },
    landmarks: [
      "Cayley Hall",
      "Stampede Grounds Cayley",
      "Cayley Access Rd",
      "Highway 2A",
      "Range Road 290",
    ],
    terrain:
      "Open grain and ranch quarters laid flat on the survey grid, the hamlet a small interruption in them, with the foothills standing well back on the western horizon.",
    windNote:
      "The southern end of the chinook belt, wide open — west and southwest wind runs across these quarters most days, and the flat ground gives it nothing to slow against.",
    seasonalWindow:
      "Big-sky light with nothing to cut it short. The quarters around the hamlet read best in standing crop and in the swath weeks; winter chinooks strip the snow off them fast, so white frames are a short window here.",
    bylawNote:
      "Launches from public land in the hamlet fall under Foothills County rules; the standard approach is takeoff and landing from the client's own property, with consent.",
    subjectMix: ["acreage-estate", "agricultural"],
    mobilisationNote:
      "About an hour south of the Calgary base on the Highway 2A run. Best treated as a half-day; High River or Nanton can share the mobilisation.",
    faqs: [
      {
        question: "Do drone flights at Cayley need NAV CANADA authorisation?",
        answer:
          "Normally, no. Cayley lies in uncontrolled Class G airspace, outside the charted Calgary-area control zones, with the nearest aerodrome at High River about 10 km north. Routine work needs no airspace authorisation here. The specific site and altitude are still confirmed against NAV Drone before every flight, as standard practice.",
      },
      {
        question: "What does Cayley actually look like from the air?",
        answer:
          "A compact grid a few blocks deep, entered off Cayley Access Rd, with Cayley Hall and the Stampede Grounds Cayley as its largest built footprints and open quarters running to the horizon on every side. That hard edge — where the streets stop and the land starts — is exactly what an aerial frame shows best.",
      },
    ],
    primaryKeywords: [
      "drone photography Cayley",
      "aerial photography Cayley",
      "Cayley drone services",
    ],
    nearestLocations: ["aldersyde", "high-river", "nanton", "longview"],
  },

  // ───────────────────────────────────────────────────────── DIAMOND VALLEY ──
  {
    slug: "diamond-valley",
    name: "Diamond Valley",
    region: "foothills-south",
    city: "Diamond Valley",
    province: "Alberta",
    coordinates: { lat: 50.69, lng: -114.245 },
    tier: 3,
    driveMinutesFromCalgary: 40,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Okotoks Air Park",
      distanceToAerodromeKm: 22,
      authorizationRequired: false,
      authorizationNote:
        "Diamond Valley lies outside the charted Calgary-area control zones in uncontrolled Class G airspace, about 22 km from Okotoks Air Park. No airspace authorisation is normally required. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "The treed river corridor between Diamond Valley's two halves runs its own calendar — leafed out before the benches in spring, gold ahead of them in autumn, and holding snow in its shade after Government Rd and Centre Ave are bare.",
      consequence:
        "The same address can read lush, golden or flat depending on the week, so a shoot booked to a diary date instead of the corridor's stage often misses the setting the town actually sells on.",
      resolution:
        "Shoots here are timed to the valley's stage, and when the colour window opens, a golden-hour hold over the corridor catches it — delivered in the standard turnaround.",
    },
    landmarks: [
      "Turner Valley Gas Plant National Historic Site",
      "Black Diamond Golf Club",
      "Government Rd",
      "Centre Ave",
      "Sheep River Falls",
    ],
    terrain:
      "Foothills bench country split by a wooded river valley — the two former townsites face each other across it, and the ranch land climbs west from the last streets toward the front ranges.",
    windNote:
      "Chinook belt, hard against the foothills. West wind comes off the front ranges with the gust spread already built in, and the open bench ground takes it at full strength.",
    seasonalWindow:
      "The valley corridor carries the seasonal story — first green in spring, a short gold window in autumn, snow lingering in its shade — while evening light rakes the benches from the west.",
    bylawNote:
      "Town of Diamond Valley park and pathway rules govern launch and landing on municipal land; private-property launch with the owner's permission is the default.",
    subjectMix: ["luxury-residential", "acreage-estate", "municipal-infrastructure"],
    mobilisationNote:
      "About 40 minutes southwest of the Calgary base. A straightforward same-day booking, often paired with Millarville or Longview work on the same trip.",
    faqs: [
      {
        question: "Is drone flight over Diamond Valley restricted?",
        answer:
          "No. Diamond Valley sits in uncontrolled Class G airspace, about 22 km from Okotoks Air Park and outside the charted Calgary-area control zones, so no airspace authorisation is normally required. The specific site and altitude are confirmed against NAV Drone before every flight, the same check that runs on every booking.",
      },
      {
        question: "When is the best time of year to photograph a Diamond Valley property?",
        answer:
          "Match the booking to the river corridor rather than the calendar. The treed valley between the town's two halves greens up ahead of the benches in spring and turns gold for a short window in autumn — the strongest colour the town shows. Shooting to that stage decides how much of the setting a listing keeps.",
      },
      {
        question: "Can the Turner Valley Gas Plant National Historic Site appear in aerial work?",
        answer:
          "As context, yes. The site reads clearly from a lawful orbit flown off adjacent property, so it can anchor a frame without being overflown. Launches stage from private property with the owner's consent, and any work over the site itself is arranged with its operators rather than assumed.",
      },
    ],
    primaryKeywords: [
      "drone photography Diamond Valley",
      "aerial photography Diamond Valley",
      "Diamond Valley drone services",
    ],
    nearestLocations: ["okotoks", "high-river", "longview", "priddis"],
  },

  // ─────────────────────────────────────────────────────────────── LONGVIEW ──
  {
    slug: "longview",
    name: "Longview",
    region: "foothills-south",
    city: "Longview",
    province: "Alberta",
    coordinates: { lat: 50.5, lng: -114.1833 },
    tier: 4,
    driveMinutesFromCalgary: 55,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "High River aerodrome",
      distanceToAerodromeKm: 24,
      authorizationRequired: false,
      authorizationNote:
        "Longview sits in uncontrolled Class G airspace, clear of the charted Calgary-area control zones and about 24 km from High River aerodrome. Authorisation is not normally required here. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Longview reads truest from altitude: the village stands on a rise with the Highwood River below it to the west and the front ranges behind that, and the long sightline in its name only exists as a stacked frame — rooftops, valley, mountains.",
      consequence:
        "A camera at street height on Morrison St sees a flat main street and a big sky — the drop to the river and the distance behind it, the two things the village is named for, never reach the picture.",
      resolution:
        "A rising reveal from village height, facing west over the Highwood valley, stacks rooftops, river and range into one frame — delivered in the standard turnaround.",
    },
    landmarks: [
      "Highwood River",
      "Hwy 22",
      "Morrison St",
      "Bar U Ranch National Historic Site",
      "Chinook Arch",
    ],
    terrain:
      "Ranch country at the front of the foothills — the village on a height of ground, the Highwood River valley cut below it, and genuine relief building west toward the mountains.",
    windNote:
      "The chinook arch overhead is a working forecast here — when it stands over the front ranges, the west wind beneath it is already crossing Hwy 22 at strength.",
    seasonalWindow:
      "Chinook winds strip snow from the open ranch ground within days, so winter frames often read brown rather than white; the Highwood corridor below the village holds colour and contrast longest.",
    bylawNote:
      "Village of Longview rules govern its public spaces and municipal land; shoots stage from private property with the owner's consent as the working default.",
    subjectMix: ["acreage-estate", "agricultural", "luxury-residential"],
    mobilisationNote:
      "About 55 minutes from the Calgary base. Practical as a half-day booking, and Diamond Valley sits on the road home if two sites need one trip.",
    faqs: [
      {
        question: "Do you need airspace authorisation for a drone shoot in Longview?",
        answer:
          "Normally, no. Longview sits in uncontrolled Class G airspace, about 24 km from High River aerodrome and outside the charted Calgary-area control zones, so routine flights need no airspace authorisation. The specific site and altitude are confirmed against NAV Drone before every flight, the same check every booking gets.",
      },
      {
        question: "Is Longview too windy for drone photography?",
        answer:
          "No, but wind sets the schedule. This stretch of Hwy 22 takes west wind straight off the front ranges, and when the chinook arch stands overhead the airframe often stays in its case. Shoots are booked against a forecast wind window rather than a fixed hour, which keeps cancellations rare.",
      },
      {
        question: "What does an aerial frame show of Longview that a ground photo cannot?",
        answer:
          "The drop and the distance. The village stands on high ground above the Highwood River, and from altitude the land falls away west toward the water with the mountains behind it — the long view the place is named for. At street level on Morrison St, none of that reaches the frame.",
      },
    ],
    primaryKeywords: [
      "drone photography Longview",
      "aerial photography Longview",
      "Longview drone services",
    ],
    nearestLocations: ["diamond-valley", "nanton", "high-river"],
  },

  // ──────────────────────────────────────────────────────────── MILLARVILLE ──
  {
    slug: "millarville",
    name: "Millarville",
    region: "foothills-south",
    city: "Foothills County",
    province: "Alberta",
    coordinates: { lat: 50.7333, lng: -114.3167 },
    tier: 4,
    driveMinutesFromCalgary: 40,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Okotoks Air Park",
      distanceToAerodromeKm: 27,
      authorizationRequired: false,
      authorizationNote:
        "In uncontrolled Class G airspace about 27 km from Okotoks Air Park, Millarville lies outside the charted Calgary-area control zones, and no authorisation is normally required. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Millarville barely exists as a townsite from the air — what reads instead is a crossroads district, acreages strung along Millarville Rd and Hwy 22, with the oval of the Millarville Racetrack as the largest built shape in the district.",
      consequence:
        "With no streetscape anywhere, a ground photograph strands a property in anonymous foothill country — nothing in frame says where it is, how it is reached, or what kind of district surrounds it.",
      resolution:
        "An approach run flown along the property's own road into a boundary-line orbit keeps one named line — Millarville Rd, Hwy 22 or the racetrack itself — in frame as a locator, delivered in the standard turnaround.",
    },
    landmarks: [
      "Millarville Racetrack",
      "Millarville Rd",
      "Hwy 22",
      "St. Anne's Anglican Church",
      "Sheep River",
    ],
    terrain:
      "Rolling, partly treed foothill country — aspen stands and open pasture folded together, with acreages strung along the roads rather than gathered into a townsite.",
    windNote:
      "Chinook belt in the first folds of the foothills. The treed rolls take some of the edge off surface gusts; above the canopy the west wind runs unbroken.",
    seasonalWindow:
      "The aspen stands set the calendar — a short, strong gold window in autumn and the freshest green in the district in spring — and snow holds in the treed folds after the open pasture has cleared.",
    bylawNote:
      "Public-land launches in the district fall under Foothills County rules; staging from the client's own property, with consent, is the standard practice.",
    subjectMix: ["acreage-estate", "luxury-residential", "agricultural"],
    mobilisationNote:
      "About 40 minutes from the Calgary base. An easy same-day booking, with Priddis or Diamond Valley close enough to share the mobilisation.",
    faqs: [
      {
        question: "Are there airspace restrictions over Millarville?",
        answer:
          "None beyond the standard rules. Millarville lies in uncontrolled Class G airspace, about 27 km from Okotoks Air Park and outside the charted Calgary-area control zones, so no airspace authorisation is normally required. The exact site and altitude are still confirmed against NAV Drone before every flight.",
      },
      {
        question: "How do you photograph a Millarville acreage when there is no town to frame it?",
        answer:
          "With the roads. Millarville is a district rather than a townsite, so a property here is located from the air by its relationship to Millarville Rd, Hwy 22 and the county grid. The orbit is planned to keep one of those lines in frame while the buildings stay large enough to read.",
      },
      {
        question: "Can the Millarville Racetrack appear in an aerial shoot?",
        answer:
          "For nearby properties, often yes. The racetrack oval is the largest built shape in the district and a genuine locator — a frame that holds it places an acreage instantly. It stays in frame as context from a lawful distance; work over the grounds themselves is arranged with the operators, not assumed.",
      },
    ],
    primaryKeywords: [
      "drone photography Millarville",
      "aerial photography Millarville",
      "Millarville drone services",
    ],
    nearestLocations: ["priddis", "okotoks", "diamond-valley", "de-winton"],
  },

  // ───────────────────────────────────────────────────────────────── NANTON ──
  {
    slug: "nanton",
    name: "Nanton",
    region: "foothills-south",
    city: "Nanton",
    province: "Alberta",
    coordinates: { lat: 50.3572, lng: -113.7742 },
    tier: 4,
    driveMinutesFromCalgary: 70,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "High River aerodrome",
      distanceToAerodromeKm: 20,
      authorizationRequired: false,
      authorizationNote:
        "Nanton lies outside the charted Calgary-area control zones in uncontrolled Class G airspace, about 20 km from High River aerodrome. No airspace authorisation is normally required. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "West wind reaches Nanton off the high country around Chain Lakes Reservoir and crosses the Hwy 2 corridor with almost nothing left to slow it — of the whole southern run, this is the ground where the wind meter matters most.",
      consequence:
        "A shoot booked here like a calendar appointment stands a real chance of being scrubbed on arrival, because the gust spread that grounds a small airframe can build in the space of an afternoon.",
      resolution:
        "Nanton work is booked into a forecast wind window — usually the morning end of the day — with the date held flexible around it, and the finished set still lands in the standard turnaround.",
    },
    landmarks: [
      "Bomber Command Museum of Canada",
      "Hwy 2",
      "Nanton Water Tower",
      "Chain Lakes Reservoir",
      "20 St",
    ],
    terrain:
      "Flat, open grain land on the survey grid running east from town, with the first ridge of the foothills rising close on the western side — prairie and hill country meeting at the townsite.",
    windNote:
      "The strongest, most persistent wind in the southern service area. West and southwest flow is the default condition; a calm day is the exception the schedule is built to catch.",
    seasonalWindow:
      "Mornings are the working end of the day here — light and wind are both usually kinder before noon — and the surrounding grain quarters carry their best colour in the weeks around harvest.",
    bylawNote:
      "Town of Nanton park and pathway rules govern municipal land — Nanton Lagoon Park is not treated as a launch site — and takeoff from private property with consent is the default.",
    subjectMix: ["agricultural", "acreage-estate", "municipal-infrastructure"],
    mobilisationNote:
      "About 70 minutes south of the Calgary base on Hwy 2. Booked as a half-day window so the shoot can move inside it with the wind; High River and Cayley sit on the same run.",
    faqs: [
      {
        question: "Does flying a drone in Nanton require authorisation?",
        answer:
          "Normally, no. Nanton sits in uncontrolled Class G airspace, about 20 km from High River aerodrome and outside the charted Calgary-area control zones, so routine flights need no airspace authorisation. The specific site and altitude are confirmed against NAV Drone before every flight, as they are everywhere we fly.",
      },
      {
        question: "Is Nanton's wind a problem for drone shoots?",
        answer:
          "Yes, and it is planned for. West wind comes down from the high country around Chain Lakes Reservoir and crosses town at full strength, so a Nanton shoot is booked against a forecast wind window — mornings are usually the calmer end — rather than a fixed hour on a calendar.",
      },
      {
        question: "Can the Bomber Command Museum of Canada be shown in aerial frames?",
        answer:
          "As townsite context, yes. The museum sits beside Hwy 2 and reads clearly from a lawful orbit flown off neighbouring property, so it can locate a commercial or residential frame at a glance. Launches stage from private property with consent, and any work over the museum itself is arranged with the museum first.",
      },
    ],
    primaryKeywords: [
      "drone photography Nanton",
      "aerial photography Nanton",
      "Nanton drone services",
    ],
    nearestLocations: ["longview", "diamond-valley", "high-river"],
  }
];
